import { View } from "react-native";
import React, { useState, useEffect } from "react";
import { useRouter, useNavigation } from "expo-router";
import { useAuth } from "@components/auth/useAuth";

import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import { ThemedProgressBar } from "@/components/ThemedProgressBar";

import { useFirebase } from "@components/firebase/useFirebase";
import { getMyGroups, createGroup } from "@/services/firebase/group-functions";
import { GroupModel, Contact } from "common247";
import { getMyContacts } from "@services/firebase/user-functions";
import { GroupEditor } from "@/components/groups/GroupEditor";
import { navigateBackOrToList } from "@/utils/navigation";

/**
 * Screen for creating a new group with linear busy indicator during initial fetch and creation.
 */
export default function NewGroupScreen() {
  const services = useFirebase();
  const { user } = useAuth(true);
  const router = useRouter();
  const navigation = useNavigation();

  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [contactsList, setContactsList] = useState<Contact[]>([]);
  const [allGroups, setAllGroups] = useState<GroupModel[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!services) return;
      setLoading(true);
      try {
        const [groupsRes, contactsRes] = await Promise.all([getMyGroups(services, {}), getMyContacts(services, { includeInactive: false })]);

        if (groupsRes.status) {
          setAllGroups(groupsRes.groups || []);
        }
        if (contactsRes.status) {
          setContactsList(contactsRes.contacts || []);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        CustomAlert.alert("Error", "Failed to load data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [services]);

  const handleSave = async (formData: any) => {
    if (!services) return;

    setIsSaving(true);
    try {
      const res = await createGroup(services, {
        ...formData,
        groupType: "standard",
      });

      if (res.status) {
        navigateBackOrToList(router, "/(tabs)/my-groups", navigation);
      } else {
        CustomAlert.alert("Error", res.error || "Failed to create group.");
      }
    } catch (error) {
      console.error("Error creating group:", error);
      CustomAlert.alert("Error", "Unexpected error while saving.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ThemedScreen title="New Group" loading={loading || isSaving}>
      <View style={{ flex: 1 }}>
        <ThemedProgressBar loading={loading} />
        {!loading && (
          <GroupEditor contactsList={contactsList} allGroups={allGroups} currentUserId={user?.uid} onSave={handleSave} onCancel={() => navigateBackOrToList(router, "/(tabs)/my-groups", navigation)} isSaving={isSaving} submitTitle="Create Group" />
        )}
      </View>
    </ThemedScreen>
  );
}
